/**
 * Camara: desplazamiento y zoom, con inercia.
 *
 * La inercia es PURAMENTE VISUAL. Afecta a como se siente mover el lienzo y a
 * nada mas: no toca ninguna posicion del mundo, no mueve a nadie de su
 * generacion, no entra en la fisica. Es una transformacion del contenedor.
 *
 * El retardo se consigue asi: el objetivo sigue al puntero al instante, y la
 * posicion que se aplica de verdad persigue al objetivo con suavizado. De ahi
 * sale el desfase sutil, que es lo que hace que no se sienta rigido. Al
 * soltar queda una velocidad que se va apagando con friccion.
 */

/** Cuanto persigue la posicion aplicada a la objetivo en cada cuadro. */
const SUAVIZADO = 0.17;
/** Cuanto persigue la escala a la suya. Algo mas rapido: el zoom brusco marea. */
const SUAVIZADO_ESCALA = 0.22;
/** Friccion del impulso al soltar. */
const FRICCION = 0.91;
/** Por debajo de esto el impulso se considera agotado. */
const IMPULSO_MINIMO = 0.02;

export const ESCALA_MINIMA = 0.045;
export const ESCALA_MAXIMA = 2.6;

export class Camara {
  /** Desplazamiento aplicado, en pixeles de pantalla. */
  x = 0;
  y = 0;
  escala = 1;

  private xObjetivo = 0;
  private yObjetivo = 0;
  private escalaObjetivo = 1;

  /** Impulso tras soltar, en pixeles de pantalla por cuadro. */
  private vx = 0;
  private vy = 0;
  private arrastrando = false;

  /**
   * Punto del mundo que debe quedarse quieto bajo el puntero mientras el zoom
   * se suaviza. Sin esto, al suavizar la escala el contenido se escurre.
   */
  private anclaMundo: { x: number; y: number } | null = null;
  private anclaPantalla: { x: number; y: number } | null = null;

  constructor(
    private ancho: number,
    private alto: number,
  ) {}

  redimensionar(ancho: number, alto: number): void {
    this.ancho = ancho;
    this.alto = alto;
  }

  // --- conversiones -------------------------------------------------------

  aPantalla(mx: number, my: number): { x: number; y: number } {
    return { x: mx * this.escala + this.x, y: my * this.escala + this.y };
  }

  aMundo(px: number, py: number): { x: number; y: number } {
    return { x: (px - this.x) / this.escala, y: (py - this.y) / this.escala };
  }

  // --- interaccion --------------------------------------------------------

  empezarArrastre(): void {
    this.arrastrando = true;
    this.vx = 0;
    this.vy = 0;
    this.anclaMundo = null;
    this.anclaPantalla = null;
  }

  /** Mueve el objetivo. El desfase con lo que se ve sale del suavizado. */
  arrastrar(dx: number, dy: number): void {
    this.xObjetivo += dx;
    this.yObjetivo += dy;
  }

  /** Al soltar, lo que quedaba de desfase se convierte en impulso. */
  soltarArrastre(): void {
    this.arrastrando = false;
    this.vx = (this.xObjetivo - this.x) * SUAVIZADO;
    this.vy = (this.yObjetivo - this.y) * SUAVIZADO;
  }

  /**
   * Zoom anclado en un punto de la pantalla: lo que esta bajo el puntero se
   * queda bajo el puntero.
   */
  zoomEn(factor: number, px: number, py: number): void {
    const antes = this.escalaObjetivo;
    const nueva = Math.min(ESCALA_MAXIMA, Math.max(ESCALA_MINIMA, antes * factor));
    if (nueva === antes) return;
    this.escalaObjetivo = nueva;
    this.anclaMundo = this.aMundo(px, py);
    this.anclaPantalla = { x: px, y: py };
  }

  /** Lleva la escala a un valor concreto, anclando en el centro de la vista. */
  ponerEscala(valor: number): void {
    this.zoomEn(
      Math.min(ESCALA_MAXIMA, Math.max(ESCALA_MINIMA, valor)) / this.escalaObjetivo,
      this.ancho / 2,
      this.alto / 2,
    );
  }

  /** Centra un punto del mundo en la vista, suavemente. */
  centrarEn(mx: number, my: number): void {
    this.xObjetivo = this.ancho / 2 - mx * this.escalaObjetivo;
    this.yObjetivo = this.alto / 2 - my * this.escalaObjetivo;
    this.anclaMundo = null;
    this.anclaPantalla = null;
    this.vx = 0;
    this.vy = 0;
  }

  /** Encaja un rectangulo del mundo en la vista, con margen. */
  encuadrar(
    lim: { x0: number; y0: number; x1: number; y1: number },
    margen = 90,
    inmediato = false,
  ): void {
    const w = Math.max(1, lim.x1 - lim.x0);
    const h = Math.max(1, lim.y1 - lim.y0);
    const e = Math.min(
      (this.ancho - margen * 2) / w,
      (this.alto - margen * 2) / h,
    );
    this.escalaObjetivo = Math.min(ESCALA_MAXIMA, Math.max(ESCALA_MINIMA, e));
    const cx = (lim.x0 + lim.x1) / 2;
    const cy = (lim.y0 + lim.y1) / 2;
    this.xObjetivo = this.ancho / 2 - cx * this.escalaObjetivo;
    this.yObjetivo = this.alto / 2 - cy * this.escalaObjetivo;
    this.anclaMundo = null;
    this.anclaPantalla = null;
    this.vx = 0;
    this.vy = 0;
    if (inmediato) {
      this.escala = this.escalaObjetivo;
      this.x = this.xObjetivo;
      this.y = this.yObjetivo;
    }
  }

  // --- cuadro -------------------------------------------------------------

  /**
   * Avanza un cuadro. Devuelve si algo cambio, para poder ahorrarse trabajo
   * cuando la camara esta quieta.
   */
  actualizar(): boolean {
    const x0 = this.x;
    const y0 = this.y;
    const e0 = this.escala;

    if (!this.arrastrando && (Math.abs(this.vx) > IMPULSO_MINIMO || Math.abs(this.vy) > IMPULSO_MINIMO)) {
      this.xObjetivo += this.vx;
      this.yObjetivo += this.vy;
      this.vx *= FRICCION;
      this.vy *= FRICCION;
    }

    this.escala += (this.escalaObjetivo - this.escala) * SUAVIZADO_ESCALA;
    if (Math.abs(this.escalaObjetivo - this.escala) < 1e-5) this.escala = this.escalaObjetivo;

    this.x += (this.xObjetivo - this.x) * SUAVIZADO;
    this.y += (this.yObjetivo - this.y) * SUAVIZADO;

    // Mantener el ancla del zoom bajo el puntero.
    if (this.anclaMundo && this.anclaPantalla) {
      const deseadoX = this.anclaPantalla.x - this.anclaMundo.x * this.escala;
      const deseadoY = this.anclaPantalla.y - this.anclaMundo.y * this.escala;
      this.x = deseadoX;
      this.y = deseadoY;
      this.xObjetivo = deseadoX;
      this.yObjetivo = deseadoY;
      if (this.escala === this.escalaObjetivo) {
        this.anclaMundo = null;
        this.anclaPantalla = null;
      }
    }

    return (
      Math.abs(this.x - x0) > 0.01 ||
      Math.abs(this.y - y0) > 0.01 ||
      Math.abs(this.escala - e0) > 1e-5
    );
  }

  /** Rectangulo del mundo que se esta viendo, para no dibujar lo de fuera. */
  vistaMundo(): { x0: number; y0: number; x1: number; y1: number } {
    const a = this.aMundo(0, 0);
    const b = this.aMundo(this.ancho, this.alto);
    return { x0: a.x, y0: a.y, x1: b.x, y1: b.y };
  }
}
